import { getStudyFullSamples, saveEcgIndex } from 'api/controllers/study-controller';
import { useCallback, useRef, useState, RefObject } from 'react';
import { downloadAnalysisXml, randomSaveEcgIndex } from '../../../helpers/helpers';
import { ECGAnalysisSource, NotificationType } from 'types/enums';
import { notificationActions } from 'utils/store/slice/notification';
import { useAppDispatch } from 'hooks';
import { logger } from 'packages/logger';

type Payload = {
  waveformId: string;
  patientName: string;
  getComparedSamples: () => {
    intEverbeatSamples: number[];
    intReferenceSamples: number[];
  };
  subjectId: string;
  onAlignmentEnd: () => void;
  assessment: string;
};

type Response = {
  onDownloadFullAnalysisSamples: () => Promise<void>;
  onDownloadAnalysisSamples: () => Promise<void>;
  onAlignmentSave: () => Promise<void>;
  isFullAnalysisLoading: boolean;
  isAnalysisLoading: boolean;
  isAlignmentSaving: boolean;
  alignmentCommentRef: RefObject<HTMLInputElement>;
};

const useStudyController = ({
  waveformId,
  patientName,
  getComparedSamples,
  subjectId,
  onAlignmentEnd,
  assessment,
}: Payload): Response => {
  const [isFullAnalysisLoading, setIsFullAnalysisLoading] = useState(false);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);
  const [isAlignmentSaving, setIsAlignmentSaving] = useState(false);
  const alignmentCommentRef = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();

  const onDownloadFullAnalysisSamples = useCallback(async () => {
    try {
      setIsFullAnalysisLoading(true);
      const { referenceIndex, referenceSamples, everbeatIndex, everbeatSamples } = await getStudyFullSamples(
        waveformId,
        patientName,
      );
      let intReferenceSamples: null | number[] = null;
      if (referenceSamples) {
        intReferenceSamples = referenceSamples.map((el) => Math.round(el));
      }

      downloadAnalysisXml({
        intEverbeatSamples: everbeatSamples,
        intReferenceSamples,
        referenceIndex,
        everbeatIndex,
        isFullSamples: true,
      });
    } catch (e) {
      logger.error('onDownloadFullAnalysisSamples', e);
    } finally {
      setIsFullAnalysisLoading(false);
    }
  }, [waveformId, patientName]);

  const onAlignmentSave = useCallback(async () => {
    setIsAlignmentSaving(true);
    try {
      const { intEverbeatSamples, intReferenceSamples } = getComparedSamples();
      await randomSaveEcgIndex({
        referenceSave: () =>
          saveEcgIndex({
            subjectId,
            source: ECGAnalysisSource.REFERENCE,
            status: 'paired',
            samples: intReferenceSamples,
            ecgWaveformId: waveformId,
            diagnosis: null,
            recordingComment: alignmentCommentRef.current?.value,
          }),

        everbeatSave: () =>
          saveEcgIndex({
            subjectId,
            source: ECGAnalysisSource.EVERBEAT,
            status: 'paired',
            samples: intEverbeatSamples,
            ecgWaveformId: waveformId,
            diagnosis: assessment,
            recordingComment: alignmentCommentRef.current?.value,
          }),
      });
    } catch (error) {
      dispatch(notificationActions.notify({ message: 'Error saving alignment', type: NotificationType.ERROR }));
      logger.error('onAlignmentSave', error);
    } finally {
      setIsAlignmentSaving(false);
      onAlignmentEnd();
    }
  }, [
    assessment,
    dispatch,
    getComparedSamples,
    onAlignmentEnd,
    setIsAlignmentSaving,
    subjectId,
    waveformId,
    alignmentCommentRef,
  ]);

  const onDownloadAnalysisSamples = useCallback(async () => {
    try {
      setIsAnalysisLoading(true);
      const { intEverbeatSamples, intReferenceSamples } = getComparedSamples();
      const { referenceIndex, everbeatIndex } = await randomSaveEcgIndex({
        referenceSave: () =>
          saveEcgIndex({
            subjectId,
            source: ECGAnalysisSource.REFERENCE,
            status: 'exported',
            samples: intReferenceSamples,
            ecgWaveformId: waveformId,
          }),
        everbeatSave: () =>
          saveEcgIndex({
            subjectId,
            source: ECGAnalysisSource.EVERBEAT,
            status: 'exported',
            samples: intEverbeatSamples,
            ecgWaveformId: waveformId,
          }),
      });
      downloadAnalysisXml({
        intEverbeatSamples,
        intReferenceSamples,
        everbeatIndex,
        referenceIndex,
      });
    } catch (error) {
      logger.error('onDownloadAnalysisSamples', error);
    } finally {
      setIsAnalysisLoading(false);
    }
  }, [getComparedSamples, subjectId, waveformId]);

  return {
    onDownloadFullAnalysisSamples,
    isFullAnalysisLoading,
    isAnalysisLoading,
    onAlignmentSave,
    onDownloadAnalysisSamples,
    isAlignmentSaving,
    alignmentCommentRef,
  };
};

export { useStudyController };
